/**
 * Reducer façade with stable method identities (usePlanEditorState pattern).
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenEffectiveConfig, DropGenMasterRef, DropGenSection } from '@trafficpulse/shared';
import { useCallback, useReducer } from 'react';

import type {
  ContainerDraft,
  DraftSendDefaults,
  DropGenHourOptimizerConfig,
} from '../../../drop-generator.types';
import { emptyDraft } from './container-draft';
import { editorReducer, type SlotPatch } from './reducer';
import type { GatewayOp } from './gateway-draft';

export function useContainerEditor() {
  const [draft, dispatch] = useReducer(editorReducer, undefined, emptyDraft);

  const reset = useCallback(
    (next: ContainerDraft) => dispatch({ type: 'reset', draft: next }),
    [],
  );
  const setField = useCallback(
    (field: keyof ContainerDraft, value: unknown) =>
      dispatch({ type: 'setField', field, value }),
    [],
  );
  const setHourOptimizer = useCallback(
    (patch: Partial<DropGenHourOptimizerConfig>) =>
      dispatch({ type: 'setHourOptimizer', patch }),
    [],
  );
  const setSendDefaults = useCallback(
    (patch: Partial<DraftSendDefaults>) =>
      dispatch({ type: 'setSendDefaults', patch }),
    [],
  );
  const slotAdd = useCallback(() => dispatch({ type: 'slotAdd' }), []);
  const slotAddMany = useCallback((count: number) => dispatch({ type: 'slotAddMany', count }), []);
  const slotMove = useCallback((from: number, to: number) => dispatch({ type: 'slotMove', from, to }), []);
  const slotsAlternate = useCallback((order?: number[]) => dispatch({ type: 'slotsAlternate', order }), []);
  const slotsAllGroups = useCallback(() => dispatch({ type: 'slotsAllGroups' }), []);
  const slotRemove = useCallback(
    (index: number) => dispatch({ type: 'slotRemove', index }),
    [],
  );
  const slotsRemove = useCallback((indexes: number[]) => dispatch({ type: 'slotsRemove', indexes }), []);
  const slotPatch = useCallback(
    (index: number, patch: SlotPatch) =>
      dispatch({ type: 'slotPatch', index, patch }),
    [],
  );
  const groupAdd = useCallback(
    (listGroupId: string) => dispatch({ type: 'groupAdd', listGroupId }),
    [],
  );
  const groupRemove = useCallback(
    (listGroupId: string) => dispatch({ type: 'groupRemove', listGroupId }),
    [],
  );
  const groupMove = useCallback(
    (from: number, to: number) => dispatch({ type: 'groupMove', from, to }),
    [],
  );
  /** One link's order mode, manual sequence or list formula (Phase 18). */
  const groupPatch = useCallback(
    (listGroupId: string, patch: Partial<ContainerDraft['groups'][number]>) =>
      dispatch({ type: 'groupPatch', listGroupId, patch }),
    [],
  );
  const gatewayOp = useCallback(
    (op: GatewayOp) => dispatch({ type: 'gateway', op }),
    [],
  );
  const creativePatch = useCallback(
    (patch: Partial<ContainerDraft['creative']>) =>
      dispatch({ type: 'creativePatch', patch }),
    [],
  );

  const verdictPatch = useCallback(
    (patch: Partial<ContainerDraft['verdict']>) =>
      dispatch({ type: 'verdictPatch', patch }),
    [],
  );

  const scalingPatch = useCallback(
    (patch: Partial<ContainerDraft['scaling']>) =>
      dispatch({ type: 'scalingPatch', patch }),
    [],
  );

  const gatewayVerdictPatch = useCallback(
    (patch: Partial<ContainerDraft['gatewayVerdict']>) => dispatch({ type: 'gatewayVerdictPatch', patch }),
    [],
  );

  const hitAgainPatch = useCallback((patch: Partial<ContainerDraft['hitAgain']>) => dispatch({ type: 'hitAgainPatch', patch }), []);

  const setSectionSource = useCallback(
    (section: DropGenSection, own: boolean, master: DropGenEffectiveConfig | null) =>
      dispatch({ type: 'setSectionSource', section, own, master }),
    [],
  );
  const setMaster = useCallback(
    (master: DropGenMasterRef | null, config: DropGenEffectiveConfig | null, campaign?: number | null) =>
      dispatch({ type: 'setMaster', master, config, campaign }),
    [],
  );

  return {
    draft,
    reset,
    setSectionSource,
    setMaster,
    setField,
    setHourOptimizer,
    setSendDefaults,
    slotAdd,
    slotAddMany,
    slotMove,
    slotsAlternate,
    slotsAllGroups,
    slotRemove,
    slotsRemove,
    slotPatch,
    groupAdd,
    groupRemove,
    groupMove,
    groupPatch,
    gatewayOp,
    creativePatch,
    verdictPatch,
    scalingPatch,
    gatewayVerdictPatch,
    hitAgainPatch,
  };
}

export type ContainerEditor = ReturnType<typeof useContainerEditor>;
